import React from "react"
import { observer } from "mobx-react-lite"

import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { Separator } from "@/components/separator"
import type { ILock } from "@/domains/lock"
import { MessageBoxIcon } from "@/domains/common/components/message-box-icon"
import { MultilineMessageBox } from "@/domains/common/components/multi-line-message-box"
import { ShortMessageBox } from "@/domains/common/components/short-message-box"
import { BodySM } from "@/domains/design/components/body-sm"
import { BodyXs } from "@/domains/design/components/body-xs"
import { cn } from "@/domains/library"

export interface ResetCardProps {
  lock: ILock
  icon?: React.ReactElement
  onReset: () => void
}

export const ResetCard = observer(({ lock, icon, onReset }: ResetCardProps) => {
  return (
    <Card.Container
      className={cn("flex w-full flex-col gap-6", "rounded-lg border border-p400-light bg-p400 p-4 lg:p-6")}
    >
      <Card.Header>
        {icon && (
          <Card.HeaderIconContainer>
            {React.cloneElement(icon, { className: "text-t200", size: 24 })}
          </Card.HeaderIconContainer>
        )}

        <div className="flex w-full flex-col">
          <Card.HeaderTitle>Reset</Card.HeaderTitle>
          <Card.HeaderDescription>Reset #{lock.id} to the default state.</Card.HeaderDescription>
        </div>
      </Card.Header>
      <Separator
        className="relative h-0 w-full overflow-visible rounded-md border border-solid border-p400-light
  after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] after:rounded-md 
  after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,43%,5%,1)] after:content-['']"
        decorative
      />

      {lock.isReset ? (
        <ShortMessageBox variant="success" icon={<MessageBoxIcon size={"md"} variant="success" />}>
          <BodyXs className="text-success-text">This veSCALE does not need a reset.</BodyXs>
        </ShortMessageBox>
      ) : lock.hasVotedThisEpoch ? (
        <MultilineMessageBox variant="error" icon={<MessageBoxIcon size={"md"} variant="error" />}>
          <BodySM className="mt-[2px]">Cannot reset this veSCALE</BodySM>
          <BodyXs className="text-error-text mt-2">
            This veSCALE has already voted this epoch. You must wait until the next epoch to reset it.
          </BodyXs>
        </MultilineMessageBox>
      ) : (
        <MultilineMessageBox variant="error" icon={<MessageBoxIcon size={"md"} variant="error" />}>
          <BodySM className="mt-[2px]">Some important notes before resetting</BodySM>
          <ul className="my-2 ml-4 list-disc">
            <li>
              <BodyXs className="text-error-text">
                Please claim rewards before resetting or all rewards will be lost.
              </BodyXs>
            </li>
            <li>
              <BodyXs className="text-error-text">
                Resetting counts as a vote and you will need to wait until next epoch to vote again.
              </BodyXs>
            </li>
            <li>
              <BodyXs className="text-error-text">
                You can create a new veSCALE and merge this one into it, to vote after resetting.
              </BodyXs>
            </li>
          </ul>
        </MultilineMessageBox>
      )}

      <div className="flex justify-end">
        <Button disabled={lock.isReset || lock.hasVotedThisEpoch} variant={"primary"} onClick={onReset}>
          Reset NFT #{lock.id}
        </Button>
      </div>
    </Card.Container>
  )
})
